import "./BillList.scss";

import clsx from "clsx";
import compact from "lodash/compact";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Button, StyledLink } from "@/components/Interface/Button/Button";
import { Pagination } from "@/components/Interface/Pagination/Pagination";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { PageNumberComboBox } from "@/components/Layout/Table/PageNumberComboBox/PageNumberComboBox";
import { TableControlGroup } from "@/components/Layout/Table/TabeControlGroup/TableControlGroup";
import { Table } from "@/components/Layout/Table/Table";
import { BillListHeader } from "@/components/Portal/Documents/BillList/components/BillListHeader/BillListHeader";
import { BillListRow } from "@/components/Portal/Documents/BillList/components/BillListRow/BillListRow";
import { useHandleError } from "@/hooks/useHandleError";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useQueryParam } from "@/hooks/useQueryParam";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { getBillFile, useGetDrBillsForCustomer } from "@/services/api";
import { GetDrBillsForCustomerSortField } from "@/services/model";
import { BillStatus } from "@/utils/enums";
import { log } from "@/utils/log";

const sortFields = [
  "Datum",
  "Abrechnungsmonat",
  "Rechnungsnummer",
  "Betrag",
  "Status",
];

export type BillListBillIdForm = {
  billIds: { [key: number]: string | boolean };
};
export const BillList: FC = () => {
  const { t } = useTranslation("portal");
  const [searchParams] = useSearchParams();
  const [isFetchingBillFile, setIsFetchingBillFile] = useState(false);
  const [isSelectMultiple, setIsSelectMultiple] = useState(false);
  const handleError = useHandleError();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile) {
      setIsSelectMultiple(false);
    }
  }, [isMobile]);

  const formMethods = useForm<BillListBillIdForm>({
    defaultValues: { billIds: {} },
  });
  const billIds = compact(Object.values(formMethods.watch("billIds")));

  const [billPage, setCurrentBillPage] = useQueryParam("page");

  const currentBillPage = Number(billPage) || 1;
  const defaultPageSize = isMobile ? 5 : 10;
  const currentBillsPerPage =
    Number(searchParams.get("pageSize")) || defaultPageSize;
  const currentSortField = searchParams.get("sortField");
  const typedCurrentSortField = sortFields.includes(`${currentSortField}`)
    ? (currentSortField as GetDrBillsForCustomerSortField)
    : undefined;
  const currentSortDirection = searchParams.get("sortDirection");
  const typedCurrentSortDirection =
    currentSortDirection === "asc"
      ? "asc"
      : currentSortDirection === "desc"
        ? "desc"
        : undefined;

  const { data: billOverview, isFetching: isFetchingBills } =
    useGetDrBillsForCustomer(
      {
        page: currentBillPage - 1,
        pageSize: currentBillsPerPage,
        sortField: typedCurrentSortField,
        sortDirection: typedCurrentSortDirection,
      },
      {
        query: {
          refetchOnWindowFocus: false,
          keepPreviousData: true,
          onSuccess: (data) => {
            // If the total number of pages is smaller than the current selected page,
            // (e.g. we are on page 3 but the data has only 2 pages now)
            // reset to the next possible value
            if (
              data.totalNumberPages &&
              data.totalNumberPages < currentBillPage
            ) {
              setCurrentBillPage(data.totalNumberPages.toString());
            }
          },
        },
      },
    );

  const downloadSelectedBills = () => {
    setIsFetchingBillFile(true);
    getBillFile({
      billId: billIds.map((id) => Number(id)),
    })
      .then((data) => {
        if (
          data.billFile?.file &&
          data.billFile.documentName &&
          data.billFile.mimeType === "application/zip"
        ) {
          const url = `data:application/zip;base64,${data.billFile?.file}`;
          const link = document.createElement("a");
          link.download = t("bills.zipName", { count: billIds.length });
          link.href = url;
          link.click();
          window.URL.revokeObjectURL(url);
        } else {
          log(
            "Could not save billFile zip, the check for valid object failed.",
          );
        }
      })
      .catch((error) => handleError(error))
      .finally(() => setIsFetchingBillFile(false));
  };

  const billPages = billOverview?.totalNumberPages || 1;
  const isLoading = isFetchingBills || isFetchingBillFile;

  const openBillsCount =
    billOverview?.bills?.filter(
      (bill) =>
        bill.status &&
        [BillStatus.overdue as string, BillStatus.reminder as string].includes(
          bill.status,
        ),
    ).length ?? 0;

  const MobileView = () => (
    <div className="flex flex-col">
      <div className="flex flex-col justify-between gap-4">
        <StyledLink
          className="accent !h-auto w-full"
          variant="outlined"
          to={portalRoutes.documents.payments}
        >
          {t("bills.managePaymentMethods")}
        </StyledLink>
      </div>

      <div className="mt-8 flex flex-col">
        <button
          className={clsx(
            "self-end text-base font-semibold text-primary-100 underline",
            isSelectMultiple ? "opacity-50" : "opacity-100",
          )}
          onClick={() => setIsSelectMultiple((prev) => !prev)}
        >
          {isSelectMultiple
            ? t("common.buttons.cancel")
            : t("bills.selectMultiple")}
        </button>

        <div className="flex flex-col">
          {billOverview?.bills?.map((bill) => (
            <BillListRow
              key={bill.id}
              bill={bill}
              variant="compact"
              isSelectMultiple={isSelectMultiple}
            />
          ))}
        </div>
      </div>
      <Pagination paginationPages={billPages} />

      {isSelectMultiple && (
        <Button
          className="accent mt-6"
          disabled={billIds.length === 0}
          onClick={downloadSelectedBills}
        >
          {t("bills.download", { count: billIds.length })}
        </Button>
      )}
    </div>
  );

  const DesktopView = () => (
    <>
      <Table
        paginationPages={billPages}
        variant="plain"
        hasPaddingBottom={false}
        data-testid="test"
      >
        <thead>
          <BillListHeader
            currentPageBillIds={
              compact(billOverview?.bills?.map((bill) => bill.id)) ?? []
            }
          />
        </thead>
        <tbody>
          {billOverview?.bills?.map((bill) => (
            <BillListRow key={bill.id} bill={bill} />
          ))}
        </tbody>
      </Table>
      <TableControlGroup>
        <div className="mr-8">
          <PageNumberComboBox />
        </div>
        <Button
          className="accent"
          disabled={Object.values(billIds).length === 0}
          onClick={downloadSelectedBills}
        >
          {t("bills.download", { count: billIds.length })}
        </Button>
      </TableControlGroup>
    </>
  );

  return (
    <div className="pt-4">
      {isLoading && <LoadingSpinner />}

      <FormProvider {...formMethods}>
        {openBillsCount > 0 && (
          <p className={clsx(isMobile && "mb-4")}>
            <Trans
              t={t}
              components={[
                <span key={0} className="font-semibold text-primary-100" />,
              ]}
              values={{ count: openBillsCount }}
              i18nKey={"bills.openBillsWarning"}
            />
          </p>
        )}
        {isMobile ? <MobileView /> : <DesktopView />}
      </FormProvider>
    </div>
  );
};
